import * as THREE from "three";

// Custom procedural solar plasma shader for the physical 3D Sun core
const sunCoreVertexShader = `
varying vec3 vNormal;
varying vec3 vWorldPosition;
varying vec3 vViewPosition;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPosition = worldPos.xyz;
  vec4 mvPosition = viewMatrix * worldPos;
  vViewPosition = -mvPosition.xyz;
  gl_Position = projectionMatrix * mvPosition;
}
`;

const sunCoreFragmentShader = `
uniform float uTime;
uniform float uAudioLow;
uniform float uAudioMid;
uniform float uAudioHigh;
uniform float uBeatPulse;
uniform float uSunScale;

varying vec3 vNormal;
varying vec3 vWorldPosition;
varying vec3 vViewPosition;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  vec3 shift = vec3(100.0);
  for (int i = 0; i < 4; ++i) {
    v += a * snoise(p);
    p = p * 2.08 + shift;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec3 posNorm = normalize(vWorldPosition);
  float timeSpeed = uTime * 0.45 + uAudioLow * 0.4;
  
  // High-frequency solar granulation & deep boiling plasma cells
  float cellNoise = fbm(posNorm * 5.5 + vec3(0.0, timeSpeed * 0.7, timeSpeed * 0.4));
  float fineNoise = fbm(posNorm * 11.0 - vec3(timeSpeed * 0.6, 0.0, timeSpeed * 0.8));
  float plasma = cellNoise * 0.65 + fineNoise * 0.35;

  // View-space Fresnel for radiant limb glow
  vec3 viewDir = normalize(vViewPosition);
  float NdotV = max(0.0, dot(vNormal, viewDir));
  float fresnel = 1.0 - NdotV;
  float rim = pow(fresnel, 2.0);

  // Intense incandescent solar color gradient:
  vec3 colSunspot  = vec3(0.85, 0.16, 0.02);   // Sunspot / dark magnetic filament
  vec3 colAmber    = vec3(1.00, 0.52, 0.08);   // Vibrant amber plasma
  vec3 colGold     = vec3(1.00, 0.88, 0.32);   // Blazing photosphere
  vec3 colCoreWhite = vec3(1.60, 1.50, 1.35);   // Superheated incandescent white-hot core

  float t = clamp(plasma * 0.5 + 0.5, 0.0, 1.0);
  vec3 color = mix(colSunspot, colAmber, smoothstep(0.12, 0.48, t));
  color = mix(color, colGold, smoothstep(0.48, 0.78, t));
  color = mix(color, colCoreWhite, smoothstep(0.78, 1.0, t) * (0.6 + uBeatPulse * 0.6));

  // Radiative limb brightening
  color += colGold * (rim * 2.5) + vec3(0.5, 0.25, 0.0) * (uAudioLow * 0.9);

  gl_FragColor = vec4(color * uSunScale, uSunScale);
}
`;

// Glowing Coronal Billboard Flare Shader
const sunFlareVertexShader = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const sunFlareFragmentShader = `
uniform float uTime;
uniform float uAudioLow;
uniform float uAudioMid;
uniform float uBeatPulse;
uniform float uSunScale;

varying vec2 vUv;

void main() {
  vec2 p = vUv - vec2(0.5);
  float r = length(p) * 2.0;
  if (r > 1.0) discard;

  // Multi-frequency radial rays
  float angle = atan(p.y, p.x);
  float rayTime = uTime * 0.8;
  float rays = sin(angle * 12.0 + rayTime) * 0.2
             + cos(angle * 24.0 - rayTime * 1.4) * 0.15
             + sin(angle * 6.0 + rayTime * 0.5) * 0.25;

  float coreGlow = exp(-r * 3.2);
  float outerHalo = pow(max(0.0, 1.0 - r), 2.0) * (1.0 + rays * 0.7);
  float totalGlow = (coreGlow * 1.6 + outerHalo * 0.9) * (1.0 + uBeatPulse * 0.6 + uAudioLow * 0.5);

  vec3 flareColor = vec3(1.0, 0.70, 0.20) * 1.8 + vec3(0.9, 0.25, 0.0) * (uAudioLow * 1.5 + uBeatPulse * 1.8);
  float alpha = clamp(totalGlow * uSunScale, 0.0, 1.0);

  gl_FragColor = vec4(flareColor * alpha, alpha);
}
`;

export class SunObject {
  constructor() {
    this.group = new THREE.Group();
    this.currentScale = 0.0;
    this.targetScale = 0.0;

    // 1) Physical Glowing 3D Photosphere Mesh (R = 100)
    const coreGeo = new THREE.SphereGeometry(100, 64, 64);
    this.coreMaterial = new THREE.ShaderMaterial({
      vertexShader: sunCoreVertexShader,
      fragmentShader: sunCoreFragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uAudioLow: { value: 0 },
        uAudioMid: { value: 0 },
        uAudioHigh: { value: 0 },
        uBeatPulse: { value: 0 },
        uSunScale: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.coreMesh = new THREE.Mesh(coreGeo, this.coreMaterial);
    this.group.add(this.coreMesh);

    // 2) Grand Additive Coronal Halo & Ray Flare Plane (size = 700)
    const flareGeo = new THREE.PlaneGeometry(700, 700);
    this.flareMaterial = new THREE.ShaderMaterial({
      vertexShader: sunFlareVertexShader,
      fragmentShader: sunFlareFragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uAudioLow: { value: 0 },
        uAudioMid: { value: 0 },
        uBeatPulse: { value: 0 },
        uSunScale: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.flareMesh = new THREE.Mesh(flareGeo, this.flareMaterial);
    this.group.add(this.flareMesh);

    this.group.scale.set(0.0001, 0.0001, 0.0001);
    this.group.visible = false;
  }

  update(dt, audio, isSunActive, camera) {
    this.targetScale = isSunActive ? 1.0 : 0.0;
    const speed = isSunActive ? 2.5 : 3.5;
    this.currentScale += (this.targetScale - this.currentScale) * Math.min(1.0, dt * speed);

    if (this.currentScale < 0.001) {
      this.currentScale = 0.0;
      this.group.visible = false;
    } else {
      this.group.visible = true;
      const s = this.currentScale;
      this.group.scale.set(s, s, s);
    }

    // Solar axial rotation for the 3D sphere
    this.coreMesh.rotation.y += dt * 0.15;
    this.coreMesh.rotation.x += dt * 0.05;

    // Billboard the outer corona flare plane toward camera
    if (camera) {
      this.flareMesh.quaternion.copy(camera.quaternion);
    }

    const time = performance.now() * 0.001;
    const beatPulse = audio?.beatPulse ?? 0;
    const aLow = audio?.low ?? 0;
    const aMid = audio?.mid ?? 0;
    const aHigh = audio?.high ?? 0;

    // Update core uniforms
    const uCore = this.coreMaterial.uniforms;
    uCore.uTime.value = time;
    uCore.uAudioLow.value = aLow;
    uCore.uAudioMid.value = aMid;
    uCore.uAudioHigh.value = aHigh;
    uCore.uBeatPulse.value = beatPulse;
    uCore.uSunScale.value = this.currentScale;

    // Update flare uniforms
    const uFlare = this.flareMaterial.uniforms;
    uFlare.uTime.value = time;
    uFlare.uAudioLow.value = aLow;
    uFlare.uAudioMid.value = aMid;
    uFlare.uBeatPulse.value = beatPulse;
    uFlare.uSunScale.value = this.currentScale;

    return this.currentScale;
  }
}
